import { EditFields, EditRow, EditValue } from "../../models/flights.model";
import { FlightEditForm } from "../form/flight-edit-form";
import { oilUnitsOf } from "../tabs/oil/oil-group";
import { FLIGHT_FUEL_FIELDS, REFUEL_FIELDS, fuelTextsOf } from "../tabs/fuel-row/fuel-row";

/** A blank text is no value, and a list of ids compares whatever its order. */
function normalized(row: Record<string, unknown>): EditFields {
    return Object.fromEntries(
        Object.entries(row).map(([key, value]) => {
            if (typeof value === "string") return [key, value.trim() || null];
            if (value instanceof Date) return [key, `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`];
            if (Array.isArray(value)) return [key, [...value].sort((a, b) => a - b)];
            if (value && typeof value === "object") return [key, JSON.stringify(value) as EditValue];
            return [key, (value ?? null) as EditValue];
        }),
    );
}

/** One value as the request would carry it, so that two of them compare as the request would. */
export function comparable(value: unknown): EditValue {
    return normalized({ value: value })["value"];
}

/** The values of the form, comparable: what the dialog opened with, and what it holds now. */
export function snapshotOf(form: FlightEditForm) {
    const value = form.getRawValue();
    return {
        flight: normalized(value.flight),
        fuel: normalized(fuelTextsOf(value.fuel, FLIGHT_FUEL_FIELDS)),
        hooks: value.hooks.map((row) => normalized(row)),
        hoists: value.hoists.map((row) => normalized(row)),
        enginePowerCheck: normalized(value.enginePowerCheck),
        penaltyFactor: normalized(value.penaltyFactor),
        refuels: value.refuels.map((row) => normalized(fuelTextsOf(row, REFUEL_FIELDS))),
        oils: value.oils.map((row) => normalized(oilUnitsOf(row))),
        crews: value.crews.map((row) => normalized(row)),
    };
}

export type FlightEditSnapshot = ReturnType<typeof snapshotOf>;

export function diff(before: EditFields, after: EditFields): EditFields {
    const changes: EditFields = {};
    for (const [key, value] of Object.entries(after)) {
        if (JSON.stringify(value) !== JSON.stringify(before[key])) changes[key] = value;
    }
    return changes;
}

/**
 * The rows of a table that changed, matched by id. A row without one is new: it sends what was filled in,
 * and nothing while it stays blank. A row the dialog opened with and no longer has is deleted.
 */
export function rowChanges(before: EditFields[], after: EditFields[]): EditRow[] {
    const known = new Map(before.filter((row) => row["id"] !== null).map((row) => [row["id"], row]));
    const blank = (row: EditFields) => Object.fromEntries(Object.keys(row).map((key) => [key, typeof row[key] === "boolean" ? false : null]));
    const changed = after
        .map((row): EditRow => {
            const { id: rowId, ...values } = row;
            const opening = (rowId === null ? null : known.get(rowId)) ?? blank(row);
            return { id: rowId as number | null, ...diff(opening, values) };
        })
        .filter((row) => Object.keys(row).length > 1);
    const kept = new Set(after.map((row) => row["id"]));
    const deleted = [...known.keys()].filter((rowId) => !kept.has(rowId)).map((rowId): EditRow => ({ id: rowId as number, deleted: true }));
    return [...changed, ...deleted];
}
